import {
  Faro,
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
} from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { setFaroUser } from "@cyngn/auth-react";
import { storageKeys } from "./localStorageUtils";
import { getRawIdToken } from "./authTokens";
import { ACTIVE_COMPANY_STORAGE_KEY } from "./observabilityHeaders";
import { authUserFromIdToken } from "./rbac";
import { FMSLogger } from "FMSLogger";
import type { PrefixedLogger } from "FMSLogger/PrefixedLogger";

let faroUtilsLogger: PrefixedLogger | undefined;

// The logger imports this module, so the prefixed logger is resolved on first
// use: at import time that cycle would still leave FMSLogger undefined.
const logger = (): PrefixedLogger => {
  if (!faroUtilsLogger) {
    faroUtilsLogger = FMSLogger.byPrefix("faroUtils");
  }
  return faroUtilsLogger;
};

let faro: Faro | undefined;

/**
 * Start Grafana Faro, which ships frontend errors, logs, web vitals and traces
 * to the Faro receiver (Grafana Cloud) that replaced Sentry.
 *
 * A missing collector URL leaves the app uninstrumented, exactly as a missing
 * Sentry DSN used to, so local runs stay offline.
 */
export const initFaro = (): Faro | undefined => {
  const collectorUrl = window._env_.REACT_APP_FARO_COLLECTOR_URL;

  if (!collectorUrl || faro) {
    return faro;
  }

  try {
    faro = initializeFaro({
      url: collectorUrl,
      apiKey: window._env_.REACT_APP_FARO_API_KEY || undefined,
      app: {
        name: window._env_.REACT_APP_FARO_APP_NAME || "fleet-app-front",
        version: window._env_.APP_VERSION_LONG || "0.0.0",
        environment: window._env_.REACT_APP_ENV || "development",
      },
      instrumentations: [
        ...getWebInstrumentations({ captureConsole: false }),
        new TracingInstrumentation(),
      ],
      sessionTracking: {
        samplingRate: Number(
          window._env_.REACT_APP_FARO_SESSION_SAMPLE_RATE || 1
        ),
      },
    });
  } catch (error) {
    logger().warn("initFaro failed", String(error));
    return undefined;
  }

  return faro;
};

/** The active Faro instance, or undefined when telemetry is not configured. */
export const getFaro = (): Faro | undefined => faro;

type TelemetryUserValue = string | number | null | undefined;

/**
 * The collector rejects a whole batch with 400 when `meta.user.id` or any
 * `meta.user.attributes` value is not a string, so every field is normalised
 * here rather than trusting the caller (`/users/me/` returns `role` as a
 * numeric primary key).
 */
const telemetryString = (value: TelemetryUserValue): string | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  return String(value);
};

// Set the Faro user context using user data
export const setTelemetryUser = (userData: {
  id?: TelemetryUserValue;
  email?: TelemetryUserValue;
  firstName?: TelemetryUserValue;
  lastName?: TelemetryUserValue;
  companyName?: TelemetryUserValue;
  role?: TelemetryUserValue;
}) => {
  logger().info("setTelemetryUser", userData);
  const firstName = telemetryString(userData.firstName);
  const lastName = telemetryString(userData.lastName);
  const company = telemetryString(userData.companyName);
  const role = telemetryString(userData.role);
  faro?.api.setUser({
    id: telemetryString(userData.id),
    email: telemetryString(userData.email),
    username: firstName && lastName ? `${firstName} ${lastName}` : undefined,
    attributes: {
      ...(company ? { company } : {}),
      ...(role ? { role } : {}),
    },
  });
};

/**
 * Set the Faro user context from localStorage (for app startup).
 *
 * A Cognito session describes itself with the plan's observability fields (`user_sub`, `role`,
 * `company_ids`, `active_company_id`, `token_age_s`), so a Faro session joins the Loki lines of
 * the requests it made; a legacy session still reports the pre-RBAC profile fields.
 */
export const setTelemetryUserFromStorage = () => {
  const cognitoUser = authUserFromIdToken(getRawIdToken());
  if (cognitoUser && faro) {
    setFaroUser(faro, cognitoUser, {
      activeCompanyId: localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY),
    });
    return;
  }

  const userId = localStorage.getItem(storageKeys.USER_ID);
  const firstName = localStorage.getItem(storageKeys.USER_FIRST_NAME);
  const lastName = localStorage.getItem(storageKeys.USER_LAST_NAME);
  const company = localStorage.getItem(storageKeys.USER_COMPANY);
  const email = localStorage.getItem(storageKeys.USER_EMAIL);
  const role = localStorage.getItem(storageKeys.USER_ROLE);

  if (userId || email) {
    setTelemetryUser({
      id: userId || undefined,
      email: email || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      companyName: company || undefined,
      role: role || undefined,
    });
  }
};

// Clear the Faro user context (for logout)
export const clearTelemetryUser = () => {
  faro?.api.resetUser();
};

export const sendTelemetryEvent = (
  error: Error,
  errorType: string,
  userText = ""
) => {
  const reported = error || new Error(errorType);

  faro?.api.pushError(reported, {
    context: {
      errorType,
      date: new Date().toISOString(),
      ...(userText ? { userText } : {}),
    },
  });
};

/**
 * Record a step leading up to an error; Faro has no breadcrumb API, so the
 * context is logged and correlated by session id instead.
 */
export const addTelemetryBreadcrumb = (
  message: string,
  data?: Record<string, string>
) => {
  faro?.api.pushLog([message], {
    level: LogLevel.INFO,
    context: data,
  });
};
